import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Settings as SettingsIcon } from "lucide-react";
import { waeAPI } from "./waeApi";
import { EmptyState, Modal, Fld, Footer, cls } from "./uiKit";

export default function WAESettings() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => waeAPI.approvalTypes().then(setRows);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="wae-settings">
      <div className="flex items-center gap-3">
        <div className="text-[13px] text-slate-500">
          Approval types are the entities the engine can orchestrate. Any module can register a new type and start creating requests through <span className="mono">POST /api/wae/v1/requests</span>.
        </div>
        <button onClick={()=>setEditing({ __new: true })} data-testid="wae-settings-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Approval Type
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {rows.length === 0
          ? <EmptyState icon={SettingsIcon} msg="No approval types configured."/>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Entity Kind</th>
                  <th className="text-left px-4 py-3">Description</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(t => (
                  <tr key={t.id} data-testid={`wae-settings-row-${t.id}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 mono text-[12px] text-slate-500">{t.code}</td>
                    <td className="px-4 py-3 font-medium text-hocp-navy">{t.name}</td>
                    <td className="px-4 py-3 mono text-[11.5px] text-slate-500">{t.entity_kind}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-500 max-w-md truncate">{t.description || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      {t.is_active
                        ? <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[11px] font-medium">Active</span>
                        : <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[11px] font-medium">Inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={()=>setEditing(t)} data-testid={`wae-settings-edit-${t.id}`}
                              className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center">
                        <Pencil className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {editing && <TypeEditor t={editing} onClose={()=>setEditing(null)} onDone={()=>{ setEditing(null); load(); }}/>}
    </div>
  );
}

function TypeEditor({ t, onClose, onDone }) {
  const isNew = t.__new || !t.id;
  const [f, setF] = useState({
    code: t.code || "", name: t.name || "", entity_kind: t.entity_kind || "",
    description: t.description || "", is_active: t.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (isNew && !f.code.match(/^[a-z0-9_.]+$/)) { toast.error("Code must be lowercase alphanumeric"); return; }
    if (!f.name || !f.entity_kind) { toast.error("Name and entity kind are required"); return; }
    setSaving(true);
    try {
      if (isNew) await waeAPI.createType(f);
      else       await waeAPI.updateType(t.id, {
        name: f.name, description: f.description, entity_kind: f.entity_kind, is_active: f.is_active,
      });
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? "New Approval Type" : `Edit · ${t.name}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Code" hint="Unique, e.g. wae.finance.refund">
          <input value={f.code} onChange={e=>setF({...f, code: e.target.value})} disabled={!isNew}
                 data-testid="wae-settings-code" className={cls}/>
        </Fld>
        <Fld label="Name">
          <input value={f.name} onChange={e=>setF({...f, name: e.target.value})}
                 data-testid="wae-settings-name" className={cls}/>
        </Fld>
        <Fld label="Entity Kind" hint="Namespace of the source module, e.g. supply_pr">
          <input value={f.entity_kind} onChange={e=>setF({...f, entity_kind: e.target.value})}
                 data-testid="wae-settings-entity" className={cls}/>
        </Fld>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
            <input type="checkbox" checked={f.is_active}
                   onChange={e=>setF({...f, is_active: e.target.checked})}
                   data-testid="wae-settings-active"/> Active
          </label>
        </div>
        <Fld label="Description" full>
          <input value={f.description} onChange={e=>setF({...f, description: e.target.value})}
                 data-testid="wae-settings-desc" className={cls}/>
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="wae-settings-save"/>
    </Modal>
  );
}
